import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App'

declare const __COMMIT__: string
declare const __SENTRY_DSN__: string

// Sentry init — no-op until VITE_SENTRY_DSN is set at build time.
// Same defense-in-depth as the API: never sends default PII, scrubs
// known-sensitive fields from breadcrumbs and event payloads.
const SENSITIVE_KEYS = new Set([
  'reporterName',
  'reporterPhone',
  'reporterEmail',
  'voiceTranscript',
  'extractionRaw',
  'notes',
  'serialNumber',
  'referenceCode',
])

function scrubKeys(obj: unknown, depth = 0): unknown {
  if (depth > 6 || obj == null) return obj
  if (Array.isArray(obj)) return obj.map((v) => scrubKeys(v, depth + 1))
  if (typeof obj !== 'object') return obj
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    out[k] = SENSITIVE_KEYS.has(k) ? '[scrubbed]' : scrubKeys(v, depth + 1)
  }
  return out
}

if (typeof __SENTRY_DSN__ === 'string' && __SENTRY_DSN__.length > 0) {
  Sentry.init({
    dsn: __SENTRY_DSN__,
    release: __COMMIT__,
    environment: 'production',
    sendDefaultPii: false,
    tracesSampleRate: 0,
    beforeSend(event) {
      if (event.request) {
        // Strip query string and reference-code path segments
        if (event.request.url) {
          event.request.url = event.request.url.replace(/\/status\/[A-Z0-9-]+/i, '/status/[ref]')
        }
        if (event.request.data) {
          event.request.data = scrubKeys(event.request.data) as typeof event.request.data
        }
      }
      if (event.contexts) {
        event.contexts = scrubKeys(event.contexts) as typeof event.contexts
      }
      if (event.extra) {
        event.extra = scrubKeys(event.extra) as typeof event.extra
      }
      return event
    },
  })
}

// Self-recovery URL: ?reset=1 nukes all client storage and reloads.
// Useful when a user has a stale session that's not auto-clearing
// (old bundle without isComplete persistence, browser-cached HTML, etc.).
// Anyone can recover by visiting https://lostblank.com/?reset=1
if (typeof window !== 'undefined' && window.location.search.includes('reset=1')) {
  try {
    localStorage.clear()
    sessionStorage.clear()
  } catch {
    // localStorage can throw if disabled (private mode in some browsers).
    // Reload anyway — the next render starts clean either way.
  }
  window.location.replace(window.location.origin + window.location.pathname)
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<div style={{ padding: 20 }}>Something went wrong. Refresh to try again.</div>}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
